// Copyright ©️ 2026 eVolve MEP, LLC

import type { BendInputKey, BendTypeName } from '@evolvemep/api-ts';

import { isNil } from 'helpers/isNotNil';
import type { BendCreatorFormValues } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';

/**
 * Center Bend sets a bend's two end straights equal, and sizes them so the cut length is the whole stick: the
 * bend sits in the middle of an uncut stick.
 *
 * It is offered for the shapes whose end straights are both free. Two fields are set for the user:
 * - `takeUpLength`, the whole bend's horizontal length. Validating with `maximizeLegLength` fills the stick with it,
 *   and the server's value is the one that counts once a bender is chosen.
 * - the obstruction's position, which splits the take up length into equal end straights.
 *
 * Under `maximizeLegLength` the server's take up length does not depend on the obstruction's position, so the
 * position follows from the take up length alone (checked against the server for all three shapes). Before a bender
 * is chosen, the take up length is estimated from the stock length and a bend radius; with the server's radius the
 * estimate matches the server to a ten-thousandth of an inch for Offset and 4-Point Saddle, and within 0.01" for
 * 3-Point Saddle.
 */
const POSITION_KEYS = {
  'Offset': 'distanceToObstruction',
  '3-Point Saddle': 'distanceToCenterOfObstruction',
  '4-Point Saddle': 'distanceToCenterOfObstruction',
} as const satisfies Partial<Record<BendTypeName, BendInputKey>>;

type CenterableBendType = keyof typeof POSITION_KEYS;

export const canCenterBend = (bendType: BendTypeName | null): bendType is CenterableBendType =>
  !isNil(bendType) && bendType in POSITION_KEYS;

/** The fields Center Bend sets for a bend type, or an empty list for a bend type it isn't offered for. */
export const getCenterBendKeys = (bendType: BendTypeName | null): readonly BendInputKey[] =>
  canCenterBend(bendType) ? ['takeUpLength', POSITION_KEYS[bendType]] : [];

const SIXTEENTH_INCH_IN_FEET = 1 / 16 / 12;
/** Rounds a length down to a sixteenth of an inch, so the part never overruns the stick. In decimal feet. */
const floorToSixteenth = (feet: number) => Math.floor(feet / SIXTEENTH_INCH_IN_FEET + 1e-6) * SIXTEENTH_INCH_IN_FEET;

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

/** How much shorter the conduit is than the path through the bend vertices, for one bend. In decimal feet. */
const bendGain = (bendRadius: number, theta: number) => 2 * bendRadius * Math.tan(theta / 2) - bendRadius * theta;

type ShapeInputs = Pick<
  BendCreatorFormValues,
  'bendType' | 'angle' | 'offsetHeight' | 'saddleHeight' | 'saddleLength' | 'materialStockLength'
>;

type Shape = {
  /** The take up length that makes the cut length equal the stock length. In decimal feet. */
  takeUpLength: number;
  /** The obstruction position that makes the end straights equal, for a given take up length. In decimal feet. */
  getPosition: (takeUpLength: number) => number;
  /** The length of each end straight, measured to the bend vertex, for a given position. In decimal feet. */
  getEndStraight: (position: number) => number;
};

/** The shape's geometry, matching `OffsetGeometry`, `ThreePointSaddleGeometry` and `FourPointSaddleGeometry`. */
const getShape = (inputs: ShapeInputs, bendRadius: number): Shape | null => {
  const { bendType, angle, offsetHeight, saddleHeight, saddleLength, materialStockLength: stock } = inputs;
  if (!canCenterBend(bendType) || isNil(angle) || isNil(stock)) return null;
  const theta = toRadians(angle);
  const gain = bendGain(bendRadius, theta);

  switch (bendType) {
    case 'Offset': {
      if (isNil(offsetHeight)) return null;
      const travel = offsetHeight / Math.sin(theta);
      const run = offsetHeight / Math.tan(theta);
      return {
        takeUpLength: stock + run - travel + 2 * gain,
        getPosition: (takeUpLength) => (takeUpLength - run) / 2,
        getEndStraight: (position) => position,
      };
    }
    case '3-Point Saddle': {
      if (isNil(saddleHeight)) return null;
      const hypotenuse = saddleHeight / Math.sin(theta) + (bendRadius * Math.tan(theta)) / 2;
      const legWidth = hypotenuse * Math.cos(theta);
      return {
        takeUpLength: stock - 2 * hypotenuse + 2 * legWidth + 2 * gain + bendGain(bendRadius, 2 * theta),
        getPosition: (takeUpLength) => takeUpLength / 2,
        getEndStraight: (position) => position - legWidth,
      };
    }
    default: {
      // 4-Point Saddle
      if (isNil(saddleHeight) || isNil(saddleLength)) return null;
      const hypotenuse = saddleHeight / Math.sin(theta);
      const legWidth = saddleHeight / Math.tan(theta);
      return {
        takeUpLength: stock - 2 * (hypotenuse - legWidth) + 4 * gain,
        getPosition: (takeUpLength) => takeUpLength / 2,
        getEndStraight: (position) => position - saddleLength / 2 - legWidth,
      };
    }
  }
};

export type CenterBendSolution =
  /** The shape's inputs aren't all entered yet. */
  | { status: 'incomplete' }
  /** The shape is too long for the stick: centred, its end straights would have no length. */
  | { status: 'tooLong' }
  | {
      status: 'fits';
      /** In decimal feet. */
      takeUpLength: number;
      /** The obstruction's position field and its value, in decimal feet. */
      position: { keyName: BendInputKey; value: number };
    };

/**
 * Solves the two fields Center Bend sets.
 * @param bendRadius The bend radius to estimate the take up length with, in decimal feet.
 * @param takeUpLength A take up length from the server, in decimal feet, which is used instead of the estimate.
 */
export const solveCenterBend = (inputs: ShapeInputs, bendRadius: number, takeUpLength?: number): CenterBendSolution => {
  const shape = getShape(inputs, bendRadius);
  if (isNil(shape) || !canCenterBend(inputs.bendType)) return { status: 'incomplete' };
  const solvedTakeUpLength = takeUpLength ?? shape.takeUpLength;
  const position = floorToSixteenth(shape.getPosition(solvedTakeUpLength));
  if (shape.getEndStraight(position) <= 0) return { status: 'tooLong' };
  return {
    status: 'fits',
    takeUpLength: solvedTakeUpLength,
    position: { keyName: POSITION_KEYS[inputs.bendType], value: position },
  };
};
